
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';

const LegalPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // Update SEO meta tags based on current language
    const metaTitles = {
      de: 'Impressum | Future Level',
      en: 'Legal Notice | Future Level',
      fr: 'Mentions Légales | Future Level',
      it: 'Note Legali | Future Level'
    };
    
    const metaDescriptions = {
      de: 'Impressum - Future Level - Unternehmensdetails und rechtliche Informationen zur KI-Videomarketing Agentur in Emmen, Schweiz',
      en: 'Legal Notice - Future Level - Company details and legal information about our AI video marketing agency in Emmen, Switzerland',
      fr: 'Mentions Légales - Future Level - Détails de l\'entreprise et informations juridiques sur notre agence de marketing vidéo IA à Emmen, Suisse',
      it: 'Note Legali - Future Level - Dettagli dell\'azienda e informazioni legali sulla nostra agenzia di video marketing AI a Emmen, Svizzera'
    };
    
    updateMetaTags({
      title: metaTitles[currentLanguage],
      description: metaDescriptions[currentLanguage],
      keywords: "Impressum, legal notice, Future Level, Video Marketing Unternehmen, Kontaktdaten, Emmen",
      canonicalUrl: getCanonicalUrl(currentLanguage === 'de' ? 'impressum' : 
                                   currentLanguage === 'en' ? 'legal' : 
                                   currentLanguage === 'fr' ? 'mentions-legales' : 'note-legali', 
                                   currentLanguage),
      language: currentLanguage,
      ogType: "website"
    });
  }, [currentLanguage, t]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-8">{t.impressum}</h1>
            
            {/* Legal content with actual company information */}
            <div className="prose max-w-none">
              <h2 className="text-2xl font-bold mb-4">Future Level</h2>
              <p className="mb-6">
                Meilipromenade 14<br />
                6032 Emmen<br />
                Schweiz
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">Kontakt</h3>
              <p className="mb-6">
                E-Mail: info@future-level.ch<br />
                Telefon: +41 44 123 45 67
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">Handelsregistereintrag</h3>
              <p className="mb-6">
                Firmenname: Future Level<br />
                Handelsregister: Handelsregisteramt Kanton Luzern<br />
                Firmennummer: CHE-XXX.XXX.XXX
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">Mehrwertsteuer</h3>
              <p className="mb-6">
                MwSt-Nummer: CHE-XXX.XXX.XXX MWST
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">Vertretungsberechtigte Person</h3>
              <p className="mb-6">
                Max Mustermann, Geschäftsführer
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">Haftungsausschluss</h3>
              <p className="mb-6">
                Die Inhalte dieser Website wurden mit größtmöglicher Sorgfalt erstellt. 
                Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte übernehmen wir jedoch keine Gewähr.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">Urheberrecht</h3>
              <p className="mb-6">
                Die durch Future Level erstellten Inhalte und Werke auf diesen Seiten unterliegen dem schweizerischen Urheberrecht. 
                Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes 
                bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">Datenschutz</h3>
              <p className="mb-6">
                Informationen zum Datenschutz finden Sie in unserer <a href={`/${currentLanguage}/${currentLanguage === 'de' ? 'datenschutz' : currentLanguage === 'en' ? 'privacy-policy' : currentLanguage === 'fr' ? 'politique-de-confidentialite' : 'informativa-privacy'}`} className="text-primary hover:underline">Datenschutzerklärung</a>.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
